import React, { useState, useEffect, useContext, useRef } from "react";
import { isString, isEmpty } from "lodash";
import { i18n } from "../../translate/i18n";
import { sendMessage } from "../../services/messages.api";
import { getSetting } from "../../services/settings.api";
import axios from "axios";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import Compressor from "compressorjs";
import useQuickMessages from "../../hooks/useQuickMessages";
import useFiles from "../../hooks/useFiles";
import logger from "../../utils/logger";
import useAudioRecorder from "./hooks/useAudioRecorder";
import useAttachmentHandlers from "./hooks/useAttachmentHandlers";
import useAgentPresence from "./hooks/useAgentPresence";
import MediaPreviewPanel from "./components/MediaPreviewPanel";
import ContactPickerModal from "./components/ContactPickerModal";
import { isBotInControl } from "../../helpers/ticketAutomation";
import { signBody } from "../../constants/whatsappProviders";

const internalSignatureCache = { value: null, expiresAt: 0 };
const INTERNAL_SIGNATURE_CACHE_TTL_MS = 5 * 60 * 1000;

async function fetchInternalSignatureEnabled() {
  const now = Date.now();
  if (internalSignatureCache.expiresAt > now && internalSignatureCache.value !== null) {
    return internalSignatureCache.value;
  }
  try {
    const { data: setting } = await getSetting("internalSignature");
    const enabled = setting?.value === "enabled";
    internalSignatureCache.value = enabled;
    internalSignatureCache.expiresAt = now + INTERNAL_SIGNATURE_CACHE_TTL_MS;
    return enabled;
  } catch (_err) {
    internalSignatureCache.value = false;
    internalSignatureCache.expiresAt = now + INTERNAL_SIGNATURE_CACHE_TTL_MS;
    return false;
  }
}

// D8.28 — Lógica (estados + effects + handlers + API + firma interna) de MessageInputCustom (chat).
export default function useMessageInput(props) {
  const { ticketStatus, ticketId, ticket, width, pendingDropFiles, onPendingDropFilesHandled } = props;
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  /** 0293 B: envío de texto en vuelo — no bloquea el input (no va en disableOption). */
  const [sendingText, setSendingText] = useState(false);
  const [quickMessages, setQuickMessages] = useState([]);
  const [options, setOptions] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [fileLists, setFileLists] = useState([]);
  const [fileListOptions, setFileListOptions] = useState([]);
  const [fileListAnchorEl, setFileListAnchorEl] = useState(null);
  const [mobileComposerMenuAnchor, setMobileComposerMenuAnchor] = useState(null);
  const mobileFabRef = useRef(null);
  const inputRef = useRef();
  const textDrainChainRef = useRef(Promise.resolve());
  /** Cola FIFO: envíos de texto encadenados tras limpiar el input (0298 Tarea 3). */
  const pendingTextJobsRef = useRef(0);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);

  // ✅ NUEVO: Función para detectar si el ticket tiene IA configurada
  /**
   * ¿Está la IA realmente al mando de este ticket?
   *
   * Tiene que coincidir con la regla del servidor (`InboundAIInvoker`), que solo
   * invoca al agente si NO hay un usuario asignado: cuando una persona toma la
   * conversación, el bot se aparta y no habla por encima.
   *
   * Faltaba justamente esa comprobación, y el desajuste dejaba el ticket MUDO: la
   * pantalla bloqueaba el teclado —«lo atiende un agente IA»— mientras el servidor
   * se apartaba por haber usuario asignado. El cliente escribía y no contestaba
   * nadie. Observado al transferir un ticket a un departamento con agente.
   */
  /**
   * 0424 F0: la definición vive en `helpers/ticketAutomation` — no se duplica aquí.
   *
   * Esta copia miraba `promptId · chatbot · useIntegration` y le faltaban dos casos:
   * `integrationId` (sin efecto medido: va siempre junto a `useIntegration`) y
   * `flowStatus`, que sí cambia el comportamiento — con un flujo corriendo el teclado
   * NO se bloqueaba y el agente podía escribir por encima del bot.
   *
   * La condición de dueño humano sigue mandando: `isBotInControl` ya la incluye, así
   * que en cuanto alguien toma la conversación el teclado se desbloquea.
   */
  const isAIAgent = isBotInControl;

  // ✅ NUEVO: Determinar si el MessageInput debe estar bloqueado
  const isMessageInputBlocked = () => {
    if (!ticket) return false;
    
    // ✅ Si el ticket tiene IA configurada, bloquear el input
    if (isAIAgent(ticket)) {
      return true;
    }
    
    // ✅ Si el ticket está en estado "closed", bloquear el input
    if (ticketStatus === "closed") {
      return true;
    }
    
    // ✅ En todos los demás casos, permitir el input
    return false;
  };

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);

  const {
    selectedMessages,
    setForwardMessageModalOpen,
    showSelectMessageCheckbox,
    setShowSelectMessageCheckbox,
    setSelectedMessages } = useContext(ForwardMessageContext);

  const { list: listQuickMessages } = useQuickMessages();
  const { list: listFiles } = useFiles();

  const {
    recording,
    analyserRef,
    handleStartRecording: _handleStartRecording,
    handleUploadAudio: _handleUploadAudio,
    handleCancelAudio: _handleCancelAudio,
  } = useAudioRecorder({
    ticketId,
    inputMessage,
    setInputMessage,
    signMessage,
    user,
    setLoading,
  });

  const { notifyTyping, notifyRecordingStart, notifyRecordingStop, notifyReset } =
    useAgentPresence({ ticketId, ticketStatus });

  // Detectar escritura del agente — useEffect evita tocar CustomInput.
  // notifyReset cuando el input se vacía para desbloquear la deduplicación
  // (ej: agente envía mensaje y escribe inmediatamente el siguiente).
  const prevInputLenRef = useRef(0);
  useEffect(() => {
    const len = inputMessage ? inputMessage.length : 0;
    if (len > prevInputLenRef.current) {
      notifyTyping();
    } else if (len === 0 && prevInputLenRef.current > 0) {
      notifyReset();
    }
    prevInputLenRef.current = len;
  }, [inputMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartRecording = async () => {
    notifyRecordingStart();
    await _handleStartRecording();
  };

  const handleUploadAudio = async () => {
    notifyRecordingStop();
    await _handleUploadAudio();
  };

  const handleCancelAudio = async () => {
    notifyRecordingStop();
    await _handleCancelAudio();
  };

  const {
    medias,
    setMedias,
    mediaCaptions,
    setMediaCaptions,
    activeMediaIndex,
    setActiveMediaIndex,
    isDragOver,
    uploadProgress,
    setUploadProgress,
    mediaCaptionInputRef,
    handleChangeMedias,
    handleInputPaste,
    handleMediaCaptionPaste,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    addFiles,
    handleRemoveMedia,
    handleSelectActiveMedia,
    handleCancelAll,
    validateFileSize,
    getFileType,
    resetAttachments,
  } = useAttachmentHandlers({ ticketStatus });

  useEffect(() => {
    if (!pendingDropFiles || pendingDropFiles.length === 0) return;
    addFiles(pendingDropFiles);
    onPendingDropFilesHandled?.();
  }, [pendingDropFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar respuestas rápidas
  useEffect(() => {
    async function fetchData() {
      const companyId = localStorage.getItem("companyId");
      const messages = await listQuickMessages({ companyId, userId: user.id });
      
      // ✅ FILTRAR: Solo mostrar respuestas rápidas activas (geral: true)
      const activeMessages = messages.filter((m) => m.geral === true);
      
      const options = activeMessages.map((m) => {
        let truncatedMessage = m.message;
        if (isString(truncatedMessage) && truncatedMessage.length > 35) {
          truncatedMessage = m.message.substring(0, 35) + "...";
        }
        return {
          value: m.message,
          label: `/${m.shortcode} ${truncatedMessage}`,
          shortcode: m.shortcode,
          message: m.message,
          mediaPath: m.mediaPath,
        };
      });
      setQuickMessages(options);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar listas de archivos
  useEffect(() => {
    async function fetchFileLists() {
      const companyId = localStorage.getItem("companyId");
      const files = await listFiles({ companyId, userId: user.id });
      const options = files.map((fileList) => {
        let truncatedName = fileList.name;
        if (isString(truncatedName) && truncatedName.length > 35) {
          truncatedName = fileList.name.substring(0, 35) + "...";
        }
        return {
          value: fileList,
          label: `📁 ${truncatedName}`,
          name: fileList.name,
          message: fileList.message,
          options: fileList.options,
        };
      });
      setFileLists(options);
    }
    fetchFileLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manejar autocompletado de respuestas rápidas
  useEffect(() => {
    if (
      isString(inputMessage) &&
      !isEmpty(inputMessage) &&
      inputMessage.startsWith("/")
    ) {
      setPopupOpen(true);

      // Si solo hay "/", mostrar todas las opciones
      if (inputMessage === "/") {
        setOptions(quickMessages);
      } else {
        // Filtrar por lo que se escriba después de "/"
        const searchTerm = inputMessage.substring(1).toLowerCase();
        const filteredOptions = quickMessages.filter(
          (m) => 
            m.shortcode.toLowerCase().includes(searchTerm) ||
            m.message.toLowerCase().includes(searchTerm)
        );
        setOptions(filteredOptions);
      }
    } else {
      setPopupOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputMessage]);

  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      resetAttachments();
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  useEffect(() => {
    textDrainChainRef.current = Promise.resolve();
    pendingTextJobsRef.current = 0;
    setSendingText(false);
  }, [ticketId]);


  // const handleChangeInput = e => {
  // 	if (isObject(e) && has(e, 'value')) {
  // 		setInputMessage(e.value);
  // 	} else {
  // 		setInputMessage(e.target.value)
  // 	}
  // };

  const handleOpenModalForward = () => {
    if (selectedMessages.length === 0) {
      setForwardMessageModalOpen(false)
      toastError(i18n.t("messagesList.header.notMessage"));
      return;
    }
    setForwardMessageModalOpen(true);
  }

  const handleAddEmoji = (e) => {
    let emoji = e.native;
    setInputMessage((prevState) => prevState + emoji);
  };


  const handleUploadQuickMessageMedia = async (blob, originalFileName, messageText = null) => {
    try {
      if (logger && logger.media) {
        logger.media.debug("Procesando multimedia para envío, tipo:", blob.type);
        logger.media.debug("Nombre original del archivo:", originalFileName);
        logger.media.debug("Texto del mensaje:", messageText);
      }
      
      // Mantener el nombre original del archivo
      let filename = originalFileName;
      
      // Si el nombre es muy extenso (más de 50 caracteres), truncarlo
              if (filename && filename.length > 50) {
          const extension = filename.split('.').pop();
          const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
          const truncatedName = nameWithoutExt.substring(0, 47) + '...';
          filename = `${truncatedName}.${extension}`;
          if (logger && logger.media) {
            logger.media.debug("Nombre truncado:", filename);
          }
        }
        
        // Si no hay nombre original, generar uno con timestamp
        if (!filename) {
          const extension = blob.type.split("/")[1] || 'bin';
          filename = `${new Date().getTime()}.${extension}`;
          if (logger && logger.media) {
            logger.media.debug("Nombre generado:", filename);
          }
        }
        
        if (logger && logger.media) {
          logger.media.debug("Nombre final del archivo:", filename);
        }

        const formData = new FormData();
        formData.append("medias", blob, filename);
        // ✅ Enviar SOLO multimedia. Si hay texto (caption) definido por el usuario, enviarlo.
        if (messageText && String(messageText).trim()) {
          formData.append("body", String(messageText).trim());
        }
        formData.append("fromMe", true);

        if (logger && logger.media) {
          logger.media.debug("Enviando multimedia al servidor...");
        }
        
        await sendMessage(ticketId, formData);
        
        if (logger && logger.media) {
          logger.media.info("Multimedia enviada exitosamente");
        }
      } catch (err) {
        if (logger && logger.media) {
          logger.media.error("Error al enviar multimedia:", err);
        }
        toastError(err);
        throw err; // Re-lanzar el error para que lo maneje la función padre
      }
  };
  
  const handleQuickAnswersClick = async (value) => {
    if (value.mediaPath) {
      setLoading(true);
      try {
        if (logger && logger.media) {
          logger.media.debug("Descargando multimedia desde:", value.mediaPath);
          logger.media.debug("URL completa:", value.mediaPath);
        }
        
        // ✅ Validar que la URL sea válida
        if (!value.mediaPath || value.mediaPath === 'null' || value.mediaPath === 'undefined' || value.mediaPath.includes('undefined')) {
          if (logger && logger.media) {
            logger.media.warn("URL de multimedia inválida:", value.mediaPath);
          }
          toastError(new Error("URL de multimedia inválida - Contacte al administrador"));
          setLoading(false);
          setInputMessage(value.value);
          return;
        }
        
        const { data } = await axios.get(value.mediaPath, {
          responseType: "blob",
          timeout: 10000, // 10 segundos de timeout
        });

        if (logger && logger.media) {
          logger.media.debug("Multimedia descargada exitosamente, tamaño:", data.size);
        }
        
        // ✅ EXTRAER NOMBRE REAL DEL ARCHIVO DESDE LA URL
        let fileName = value.value; // Fallback al texto de respuesta rápida
        
        if (value.mediaPath) {
          // Extraer nombre desde la URL del archivo
          const urlParts = value.mediaPath.split('/');
          const lastPart = urlParts[urlParts.length - 1];
          
          if (lastPart && lastPart.includes('.')) {
            fileName = lastPart;
            if (logger && logger.media) {
              logger.media.debug("Nombre extraído desde URL:", fileName);
            }
          }
        }
        
        // ✅ CORRECTO: Pasar el texto del mensaje junto con la imagen
        await handleUploadQuickMessageMedia(data, fileName, value.value);
        setInputMessage("");
        setLoading(false);
        return;
      } catch (err) {
        console.error("Error al procesar multimedia de respuesta rápida:", err);
        console.error("URL que falló:", value.mediaPath);
        
        // Mostrar error más específico
        let errorMessage = "Error al procesar multimedia";
        if (err.response) {
          if (err.response.status === 404) {
            errorMessage = "Archivo multimedia no encontrado";
          } else if (err.response.status === 403) {
            errorMessage = "Sin permisos para acceder al archivo";
          } else {
            errorMessage = `Error del servidor: ${err.response.status}`;
          }
        } else if (err.code === 'ECONNABORTED') {
          errorMessage = "Tiempo de espera agotado";
        }
        
        toastError(new Error(errorMessage));
        setLoading(false);
        
        // Si falla la multimedia, al menos enviar el texto
        setInputMessage(value.value);
        return;
      }
    }

    setInputMessage("");
    setInputMessage(value.value);
  };

  const handleFileListClick = async (value) => {
    if (logger && logger.media) {
      logger.media.debug("Valor recibido en handleFileListClick:", value);
    }
    
    // Verificar si value.value.options existe (estructura correcta)
    if (!value.value || !value.value.options || value.value.options.length === 0) {
      if (logger && logger.media) {
        logger.media.warn("No hay archivos en la lista. value.value:", value.value);
      }
      toastError("Esta lista de archivos no contiene archivos");
      return;
    }

    setLoading(true);
    try {
      if (logger && logger.media) {
        logger.media.debug("Enviando lista de archivos:", value.name);
        logger.media.debug("Archivos en la lista:", value.value.options.length);
      }

      // Enviar cada archivo de la lista
      for (const fileOption of value.value.options) {
        try {
          if (logger && logger.media) {
            logger.media.debug("Procesando archivo:", fileOption.name);
          }
          
          // Construir URL del archivo
          const publicFolder = "public/fileList";
          const backendUrl = window.WATOOLX_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
          if (!backendUrl) {
            throw new Error('REACT_APP_BACKEND_URL no está configurado');
          }
          const fileUrl = `${backendUrl}/${publicFolder}/${value.value.id}/${fileOption.path}`;
          
          if (logger && logger.media) {
            logger.media.debug("URL del archivo:", fileUrl);
          }
          
          // Extraer nombre del archivo desde la ruta
          const fileName = fileOption.path || fileOption.name || 'documento';
          if (logger && logger.media) {
            logger.media.debug("Nombre del archivo extraído:", fileName);
          }
          
          // Descargar archivo
          const { data } = await axios.get(fileUrl, {
            responseType: "blob",
            timeout: 10000,
          });

          if (logger && logger.media) {
            logger.media.debug("Archivo descargado:", fileName, "tamaño:", data.size);
          }
          
          // Enviar archivo
          await handleUploadQuickMessageMedia(data, fileName);
          
          // Pequeña pausa entre archivos para no sobrecargar
          await new Promise(resolve => setTimeout(resolve, 500));
          
        } catch (err) {
          if (logger && logger.media) {
            logger.media.error("Error procesando archivo:", fileOption.name, err);
          }
          toastError(`Error al procesar archivo: ${fileOption.name}`);
        }
      }
      
      if (logger && logger.media) {
        logger.media.info("Lista de archivos enviada exitosamente");
      }
      setLoading(false);
      
    } catch (err) {
      if (logger && logger.media) {
        logger.media.error("Error al enviar lista de archivos:", err);
      }
      toastError("Error al enviar lista de archivos");
      setLoading(false);
    }
  };

  const handleUploadMedia = async (e) => {
    setLoading(true);
    e.preventDefault();

    const internalSignatureEnabled = await fetchInternalSignatureEnabled();

    // ✅ NUEVO: Crear mensaje interno con el nombre del usuario si está activada (solo una vez antes del primer archivo)
    if (internalSignatureEnabled && user?.name && medias.length > 0) {
      try {
        const internalMessage = {
          read: 1,
          fromMe: true,
          mediaUrl: "",
          body: user.name, // ✅ Nombre dinámico del usuario actual
          mediaType: "internal_signature", // ✅ Nuevo tipo de mensaje interno
          quotedMsg: null,
        };
        await sendMessage(ticketId, internalMessage);
      } catch (err) {
        // No fallar si no se puede crear el mensaje interno
        console.error("Error creando mensaje interno:", err);
      }
    }

    // Correo (doc 0449 §4.1): texto y archivos en UNA petición, y por tanto en UN solo correo. Sin
    // comprimir: son adjuntos, no fotos de chat. Antes salía un correo por archivo, con el texto repetido.
    if (ticket?.whatsapp?.provider === "email" && medias.length > 0) {
      const texto = medias.map((_, i) => (mediaCaptions[i] ?? "").trim()).filter(Boolean).join("\n\n");
      const formData = new FormData();
      formData.append("fromMe", true);
      medias.forEach((m) => formData.append("medias", m));
      if (texto) formData.append("body", texto);
      // La firma la pone el backend (doc 0449 §4.3).
      formData.append("sign", Boolean(signMessage));
      try {
        await sendMessage(ticketId, formData, {
          onUploadProgress: (event) => {
            const progress = Math.round((event.loaded * 100) / event.total);
            setUploadProgress(Object.fromEntries(medias.map((_, i) => [i, progress])));
          },
        });
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
      setMedias([]);
      setMediaCaptions([]);
      setUploadProgress({});
      return;
    }

    // ✅ SI TODOS LOS ARCHIVOS SON VÁLIDOS, CONTINUAR CON EL ENVÍO
    // ✅ MEJORA: Enviar cada archivo por separado
    for (let i = 0; i < medias.length; i++) {
      const media = medias[i];
      const currentIndex = i; // Capturar el índice para evitar problemas de closure
      const rawCaption = (mediaCaptions[i] ?? "").trim();
      const captionBody = rawCaption
        ? signMessage && user?.name
          ? signBody(ticket?.whatsapp?.provider, user.name, rawCaption)
          : rawCaption
        : "";

      try {
        // Actualizar progreso
        setUploadProgress(prev => ({
          ...prev,
          [currentIndex]: 0
        }));

        if (media?.type.split('/')[0] === 'image') {
          // ✅ MEJORA: Manejar compresión de imágenes correctamente
          await new Promise((resolve, reject) => {
            new Compressor(media, {
              quality: 0.7,
              async success(compressedMedia) {
                try {
                  const formData = new FormData();
                  formData.append("fromMe", true);
                  formData.append("medias", compressedMedia);
                  // ✅ Enviar SOLO multimedia. Si hay caption, enviarlo.
                  if (captionBody) {
                    formData.append("body", captionBody);
                  }

                  await sendMessage(ticketId, formData, {
                    onUploadProgress: (event) => {
                      const progress = Math.round((event.loaded * 100) / event.total);
                      setUploadProgress(prev => ({
                        ...prev,
                        [currentIndex]: progress
                      }));
                    },
                  });

                  resolve();
                } catch (err) {
                  reject(err);
                }
              },
              error(err) {
                reject(err);
              },
            });
          });
        } else {
          // ✅ MEJORA: Enviar archivos no-imagen directamente
          const formData = new FormData();
          formData.append("fromMe", true);
          formData.append("medias", media);
          // ✅ Enviar SOLO multimedia. Si hay caption, enviarlo.
          if (captionBody) {
            formData.append("body", captionBody);
          }
          // ✅ AGREGAR: Tamaño del archivo para el backend
          formData.append("fileSize", media.size);
          formData.append("fileName", media.name);

          await sendMessage(ticketId, formData, {
            onUploadProgress: (event) => {
              const progress = Math.round((event.loaded * 100) / event.total);
              setUploadProgress(prev => ({
                ...prev,
                [currentIndex]: progress
              }));
            },
          });
        }
      } catch (err) {
        toastError(err);
      }
    }

    setLoading(false);
    setMedias([]);
    setMediaCaptions([]);
    setUploadProgress({});
  }

  const runTextSendJob = async ({
    enqueueTicketId,
    trimmed,
    quotedSnapshot,
    signSnapshot,
    signerName,
  }) => {
    // Correo (doc 0449 §4.3): no se pega el nombre en el texto; el backend añade la firma del agente.
    const esCorreo = ticket?.whatsapp?.provider === "email";
    const bodyForApi =
      !esCorreo && signSnapshot && signerName
        ? signBody(ticket?.whatsapp?.provider, signerName, trimmed)
        : trimmed;
    try {
      const internalSignatureEnabled = await fetchInternalSignatureEnabled();

      if (internalSignatureEnabled && signerName) {
        const internalMessage = {
          read: 1,
          fromMe: true,
          mediaUrl: "",
          body: signerName,
          mediaType: "internal_signature",
          quotedMsg: null,
        };
        await sendMessage(enqueueTicketId, internalMessage);
      }

      const message = {
        read: 1,
        fromMe: true,
        mediaUrl: "",
        body: bodyForApi,
        quotedMsg: quotedSnapshot,
        ...(esCorreo ? { sign: Boolean(signSnapshot) } : {}),
      };
      await sendMessage(enqueueTicketId, message);

      setShowEmoji(false);
      setReplyingMessage(null);
    } catch (err) {
      toastError(err);
      setInputMessage((prev) =>
        prev && prev.trim() ? `${trimmed}\n${prev}` : trimmed
      );
    }
  };

  const handleSendMessage = async () => {
    const trimmed = inputMessage.trim();
    if (trimmed === "") return;

    const quotedSnapshot = replyingMessage;
    const signSnapshot = signMessage;
    const signerName = user?.name || "";
    const enqueueTicketId = ticketId;

    setInputMessage("");

    pendingTextJobsRef.current += 1;
    setSendingText(true);

    textDrainChainRef.current = textDrainChainRef.current
      .then(() => runTextSendJob({
        enqueueTicketId,
        trimmed,
        quotedSnapshot,
        signSnapshot,
        signerName,
      }))
      .catch(() => {})
      .finally(() => {
        pendingTextJobsRef.current = Math.max(0, pendingTextJobsRef.current - 1);
        if (pendingTextJobsRef.current === 0) {
          setSendingText(false);
        }
      });
  };


  const handleQuickReplyButtonClick = () => {
    setInputMessage("/");
    // Forzar que se muestren las opciones inmediatamente
    setTimeout(() => {
      setPopupOpen(true);
      setOptions(quickMessages);
    }, 100);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleFileListButtonClick = (event) => {
    setFileListAnchorEl(event.currentTarget);
    setFileListOptions(fileLists);
  };

  const handleFileListMenuClose = () => {
    setFileListAnchorEl(null);
  };

  const handleFileListSelect = (fileList) => {
    handleFileListClick(fileList);
    handleFileListMenuClose();
  };

  const closeMobileComposerMenu = () => setMobileComposerMenuAnchor(null);

  const mobilePickDocument = () => {
    closeMobileComposerMenu();
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = ".pdf,.doc,.docx,.xls,.xlsx,.txt,.ppt,.pptx";
    input.onchange = (e) => handleChangeMedias(e);
    input.click();
  };
  const mobilePickPhotoVideo = () => {
    closeMobileComposerMenu();
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "image/*,video/*";
    input.onchange = (e) => handleChangeMedias(e);
    input.click();
  };
  const mobilePickAudio = () => {
    closeMobileComposerMenu();
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "audio/*";
    input.onchange = (e) => handleChangeMedias(e);
    input.click();
  };
  const [contactPickerOpen, setContactPickerOpen] = useState(false);

  const mobilePickContact = () => {
    closeMobileComposerMenu();
    setContactPickerOpen(true);
  };

  const handleSendVcard = async (contacts) => {
    if (!contacts || contacts.length === 0) return;
    setLoading(true);
    try {
      await sendMessage(ticketId, {
        body: JSON.stringify(contacts),
        mediaType: "contactMessage",
        fromMe: true,
        read: 1,
      });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const disableOption = () => {
    // ✅ Deshabilitar si está cargando (multimedia/grabación), grabando, o el ticket no está abierto
    // ✅ 0293 B: sendingText NO deshabilita — el usuario puede seguir escribiendo el siguiente mensaje
    if (loading || recording || ticketStatus !== "open") {
      return true;
    }
    
    // ✅ CRÍTICO: Deshabilitar si el ticket está en "pending" sin usuario asignado
    // Esto evita que el usuario escriba cuando el ticket está pendiente de aceptación
    if (ticketStatus === "pending" && (!ticket?.userId || ticket.userId === null)) {
      return true;
    }
    
    return false;
  };

  const blocked = isMessageInputBlocked();
  const isPending = ticket?.status === "pending";
  const isAIBlocked = blocked && isAIAgent(ticket);

  const contactPickerModal = (
    <ContactPickerModal
      open={contactPickerOpen}
      onClose={() => setContactPickerOpen(false)}
      onSend={handleSendVcard}
    />
  );

  if (medias.length > 0)
    return (
      <>
        {contactPickerModal}
      <MediaPreviewPanel
        medias={medias}
        activeMediaIndex={activeMediaIndex}
        handleSelectActiveMedia={handleSelectActiveMedia}
        handleRemoveMedia={handleRemoveMedia}
        uploadProgress={uploadProgress}
        loading={loading}
        recording={recording}
        ticketStatus={ticketStatus}
        handleChangeMedias={handleChangeMedias}
        isDragOver={isDragOver}
        handleDragOver={handleDragOver}
        handleDragEnter={handleDragEnter}
        handleDragLeave={handleDragLeave}
        handleDrop={handleDrop}
        getFileType={getFileType}
        mediaCaptions={mediaCaptions}
        mediaCaptionInputRef={mediaCaptionInputRef}
        setMediaCaptions={setMediaCaptions}
        handleMediaCaptionPaste={handleMediaCaptionPaste}
        handleCancelAll={handleCancelAll}
        handleUploadMedia={handleUploadMedia}
      />
      </>
    );

  return {
    _handleCancelAudio,
    _handleStartRecording,
    _handleUploadAudio,
    activeMediaIndex,
    addFiles,
    analyserRef,
    blocked,
    closeMobileComposerMenu,
    contactPickerModal,
    contactPickerOpen,
    disableOption,
    fileListAnchorEl,
    fileListOptions,
    fileLists,
    getFileType,
    handleAddEmoji,
    handleCancelAll,
    handleCancelAudio,
    handleChangeMedias,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileListButtonClick,
    handleFileListClick,
    handleFileListMenuClose,
    handleFileListSelect,
    handleInputPaste,
    handleMediaCaptionPaste,
    handleOpenModalForward,
    handleQuickAnswersClick,
    handleQuickReplyButtonClick,
    handleRemoveMedia,
    handleSelectActiveMedia,
    handleSendMessage,
    handleSendVcard,
    handleStartRecording,
    handleUploadAudio,
    handleUploadMedia,
    handleUploadQuickMessageMedia,
    inputMessage,
    inputRef,
    isAIAgent,
    isAIBlocked,
    isDragOver,
    isMessageInputBlocked,
    isPending,
    listFiles,
    listQuickMessages,
    loading,
    mediaCaptionInputRef,
    mediaCaptions,
    medias,
    mobileComposerMenuAnchor,
    mobileFabRef,
    mobilePickAudio,
    mobilePickContact,
    mobilePickDocument,
    mobilePickPhotoVideo,
    notifyRecordingStart,
    notifyRecordingStop,
    notifyReset,
    notifyTyping,
    options,
    pendingTextJobsRef,
    popupOpen,
    prevInputLenRef,
    quickMessages,
    recording,
    replyingMessage,
    resetAttachments,
    runTextSendJob,
    selectedMessages,
    sendingText,
    setActiveMediaIndex,
    setContactPickerOpen,
    setFileListAnchorEl,
    setFileListOptions,
    setFileLists,
    setForwardMessageModalOpen,
    setInputMessage,
    setLoading,
    setMediaCaptions,
    setMedias,
    setMobileComposerMenuAnchor,
    setOptions,
    setPopupOpen,
    setQuickMessages,
    setReplyingMessage,
    setSelectedMessages,
    setSendingText,
    setShowEmoji,
    setShowSelectMessageCheckbox,
    setSignMessage,
    setUploadProgress,
    showEmoji,
    showSelectMessageCheckbox,
    signMessage,
    textDrainChainRef,
    uploadProgress,
    user,
    validateFileSize,
  };
}
